import { runMigrations } from '@kapture/sync-core';
import { resetAndSeed, seedIfEmpty } from '@kapture/domain';
import { openDatabase } from './sqlite';

let ready: Promise<void> | null = null;

/**
 * Call once at app bootstrap — opens the shared SQLite connection,
 * applies pending migrations, and seeds demo data on first launch.
 * Subsequent callers receive the cached promise so there's a single
 * init pass per page load.
 */
export function initDb(): Promise<void> {
  if (!ready) {
    ready = (async () => {
      const info = await openDatabase();
      if (!info.persistent) {
        // Worth saying out loud: an offline-first app on a volatile database
        // loses the day's captures when the tab reloads.
        console.warn(`[db] storage is not durable (vfs: ${info.vfs})`);
      }
      // Published in every build, not just dev. It is two read-only strings,
      // no way to reach the database through it, and it is what lets an e2e
      // test — and a support engineer looking at a real device — tell durable
      // storage from the in-memory fallback. That distinction was invisible
      // for the whole time the app was silently running on `:memory:`.
      (globalThis as unknown as { __kaptureDb?: typeof info }).__kaptureDb = info;

      await runMigrations();
      await seedIfEmpty();
      if (import.meta.env.DEV) {
        // Expose a dev helper so you can wipe+reseed from the console.
        (globalThis as unknown as { __kapture?: { resetSeed: () => Promise<void> } }).__kapture = {
          resetSeed: resetAndSeed,
        };
      }
    })();
  }
  return ready;
}
